import React, { useCallback, useState } from 'react'
import { Drawer, message, Spin, Tabs } from 'antd'
import _ from 'lodash'
import {
  flattenUniqueItems,
  getFlattenedPublishedIDs,
  getFlattenedUnpublishedIDs,
  generateStoreModifierInsertParams,
  generateStoreVariantInsertParams
} from './helpers'
import {
  InventoryGroup,
  InventoryPublishDrawerProps,
  ModifierGroup,
  SortedCategory,
  WithChanges
} from './types'
import InventoryChangesModal from './InventoryChangesModal'
import ProductVariants from './InventoryPublisher/ProductVariants'
import Modifiers from './InventoryPublisher/Modifiers'
import Actions from './InventoryPublisher/Actions'
import styled from '@emotion/styled'

const { TabPane } = Tabs

const InventoryPublishDrawer = (props: InventoryPublishDrawerProps) => {
  const [variants, setVariants] = useState<InventoryGroup[]>([])
  const [pristineVariants, setPristineVariants] = useState<InventoryGroup[]>([])
  const [modifiers, setModifiers] = useState<InventoryGroup[]>([])
  const [pristineModifiers, setPristineModifiers] = useState<InventoryGroup[]>(
    []
  )
  const [modifierGroupsData, setModifierGroupsData] = useState<ModifierGroup[]>(
    []
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [variantsWithChanges, setVariantsWithChanges] = useState<WithChanges>()
  const [modifiersWithChanges, setModifiersWithChanges] =
    useState<WithChanges>()

  const [sortedVariantGroups, setSortedVariantGroups] = useState<
    SortedCategory[]
  >([])
  const [sortedModifierGroups, setSortedModifierGroups] = useState<
    InventoryGroup[]
  >([])

  const handleDetermineChanges = useCallback(
    (original: InventoryGroup[], change: InventoryGroup[]) => {
      const originals = flattenUniqueItems(original)
      const changes = flattenUniqueItems(change)

      const publishedItems = changes.filter((c) => {
        const item = originals.find((o) => o.id === c.id)

        return item?.isPublished !== c.isPublished && !item?.isPublished
      })

      const unpublishedItems = changes.filter((c) => {
        const item = originals.find((o) => o.id === c.id)

        return item?.isPublished !== c.isPublished && item?.isPublished
      })

      return {
        publishedItems: publishedItems,
        unpublishedItems: unpublishedItems
      }
    },
    [variants, modifiers, pristineVariants, pristineModifiers]
  )

  const saveChanges = () => {
    const {
      publishStoreModifiers,
      unpublishStoreModifiers,
      publishStoreVariants,
      unpublishStoreVariants,
      createStoreModifiers,
      createStoreVariants,
      isPreorder,
      updateStoreCategoriesArrangements,
      updateStoreModifierGroupArrangements
    } = props

    const [orphanedVariants, persistedVariants] = _.partition(
      flattenUniqueItems(variants),
      ({ orphaned }) => !!orphaned
    )
    const publishedVariantIds = _.uniq(
      getFlattenedPublishedIDs(persistedVariants)
    )
    const unpublishedVariantIds = _.uniq(
      getFlattenedUnpublishedIDs(persistedVariants)
    )
    const publishedOrphanedVariantIds = _.uniq(
      getFlattenedPublishedIDs(orphanedVariants)
    )
    const unPublishedOrphanedVariantIds = _.uniq(
      getFlattenedUnpublishedIDs(orphanedVariants)
    )

    const [orphanedModifiers, persistedModifiers] = _.partition(
      flattenUniqueItems(modifiers),
      ({ orphaned }) => !!orphaned
    )
    const publishedModifierIds = _.uniq(
      getFlattenedPublishedIDs(persistedModifiers)
    )
    const unpublishedModifierIds = _.uniq(
      getFlattenedUnpublishedIDs(persistedModifiers)
    )
    const publishedOrphanedModifierIds = _.uniq(
      getFlattenedPublishedIDs(orphanedModifiers)
    )
    const unPublishedOrphanedModifierIds = _.uniq(
      getFlattenedUnpublishedIDs(orphanedModifiers)
    )

    const categoriesArrangementsInputs = sortedVariantGroups.map((group) => {
      return {
        categoryId: group.id,
        productIds: group.products.map((product) => {
          return product.id
        })
      }
    })

    const modifierGroupArrangementInputs = sortedModifierGroups.map((group) => {
      return {
        modifierGroupId: group.id,
        modifierIds: group.items.map((item) => {
          return item.id
        })
      }
    })

    setIsLoading(true)
    Promise.all([
      publishedVariantIds.length &&
        publishStoreVariants({
          variables: {
            variantIds: publishedVariantIds,
            storeId: props.storeInfo.id
          }
        }),
      unpublishedVariantIds.length &&
        unpublishStoreVariants({
          variables: {
            variantIds: unpublishedVariantIds,
            storeId: props.storeInfo.id
          }
        }),
      publishedOrphanedVariantIds.length &&
        createStoreVariants({
          variables: {
            storeVariants: generateStoreVariantInsertParams({
              variantIds: publishedOrphanedVariantIds,
              storeId: props.storeInfo.id,
              isPublished: true,
              isPreorder,
              inStock: true
            })
          }
        }),
      unPublishedOrphanedVariantIds.length &&
        createStoreVariants({
          variables: {
            storeVariants: generateStoreVariantInsertParams({
              variantIds: unPublishedOrphanedVariantIds,
              storeId: props.storeInfo.id,
              isPublished: false,
              isPreorder,
              inStock: true
            })
          }
        }),
      publishedModifierIds.length &&
        publishStoreModifiers({
          variables: {
            modifierIds: publishedModifierIds,
            storeId: props.storeInfo.id
          }
        }),
      unpublishedModifierIds.length &&
        unpublishStoreModifiers({
          variables: {
            modifierIds: unpublishedModifierIds,
            storeId: props.storeInfo.id
          }
        }),
      publishedOrphanedModifierIds.length &&
        createStoreModifiers({
          variables: {
            storeModifiers: generateStoreModifierInsertParams({
              modifierIds: publishedOrphanedModifierIds,
              storeId: props.storeInfo.id,
              isPublished: true,
              isPreorder,
              inStock: true
            })
          }
        }),
      unPublishedOrphanedModifierIds.length &&
        createStoreModifiers({
          variables: {
            storeModifiers: generateStoreModifierInsertParams({
              modifierIds: unPublishedOrphanedModifierIds,
              storeId: props.storeInfo.id,
              isPublished: false,
              isPreorder,
              inStock: true
            })
          }
        }),
      sortedVariantGroups.length &&
        updateStoreCategoriesArrangements({
          variables: {
            categoriesArrangementsInputs: categoriesArrangementsInputs,
            storeId: props.storeInfo.id,
            orderType: isPreorder ? 'PREORDER' : 'SAMEDAY'
          }
        }),
      sortedModifierGroups.length &&
        updateStoreModifierGroupArrangements({
          variables: {
            modifierGroupArrangementInputs: modifierGroupArrangementInputs,
            storeId: props.storeInfo.id,
            orderType: isPreorder ? 'PREORDER' : 'SAMEDAY'
          }
        })
    ])
      .then(() => props.refetchStoreInventory())
      .then(() => {
        message.success('Inventory updated successfully!', 1)
        setShowModal(false)
      })
      .catch((error: Error) => {
        message.error(`Failed to update inventory due to ${error.message}`, 3)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleOnDiscard = () => {
    props.onClose()
  }

  const handleChangesChecks = () => {
    const variantChanges = handleDetermineChanges(pristineVariants, variants)
    const modifierChanges = handleDetermineChanges(pristineModifiers, modifiers)

    const {
      publishedItems: publishedVariants,
      unpublishedItems: unpublishedVariants
    } = variantChanges
    const {
      publishedItems: publishedModifiers,
      unpublishedItems: unpublishedModifiers
    } = modifierChanges

    if (
      !publishedVariants.length &&
      !publishedModifiers.length &&
      !unpublishedVariants.length &&
      !unpublishedModifiers.length &&
      !sortedVariantGroups.length &&
      !sortedModifierGroups.length
    )
      return

    setVariantsWithChanges(variantChanges)
    setModifiersWithChanges(modifierChanges)
    setShowModal(true)
  }

  const drawerTitle = `${
    props.isPreorder ? 'Pre-Order' : 'Same Day'
  } Inventory #${
    props.storeInfo.name.length > 30
      ? props.storeInfo.name.slice(0, 29) + '...'
      : props.storeInfo.name
  }`

  return (
    <StyledDrawer
      open={props.isVisible}
      onClose={props.onClose}
      width={708}
      title={drawerTitle}
    >
      <InventoryChangesModal
        visible={showModal}
        onCancel={setShowModal}
        onOk={saveChanges}
        loading={isLoading}
        variantsWithChanges={variantsWithChanges}
        modifiersWithChanges={modifiersWithChanges}
        modifierGroupsData={modifierGroupsData as ModifierGroup[]}
      />
      <Spin spinning={isLoading}>
        <LocationInventoryPublisher>
          <div
            className='location-inventory-publisher-header'
            style={{ position: 'fixed', top: '80px', right: 0, width: 708 }}
          >
            <Actions
              discardHandler={handleOnDiscard}
              saveHandler={handleChangesChecks}
              loading={isLoading}
            />
          </div>
          <Tabs
            activeKey={props.activeTab || 'products'}
            onChange={props.onTabChange}
          >
            <TabPane tab='Products' key='products'>
              <ProductVariants
                isPreorder={props.isPreorder}
                storeId={props.storeInfo.id}
                sortedGroups={sortedVariantGroups}
                setMasterVariants={setVariants}
                setMasterPristineVariants={setPristineVariants}
                setSortedGroups={setSortedVariantGroups}
              />
            </TabPane>
            <TabPane tab='Modifiers' key='modifiers'>
              <Modifiers
                isPreorder={props.isPreorder}
                storeId={props.storeInfo.id}
                sortedGroups={sortedModifierGroups}
                setMasterModifiers={setModifiers}
                setMasterPristineModifiers={setPristineModifiers}
                setMasterModifierGroups={setModifierGroupsData}
                setSortedGroups={setSortedModifierGroups}
              />
            </TabPane>
          </Tabs>
        </LocationInventoryPublisher>
      </Spin>
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    align-items: flex-start;
    background: transparent;
    min-height: 80px;
    margin-bottom: 40px;
  }

  .ant-drawer-header .ant-drawer-header-title > button {
    order: 2;
  }

  .ant-drawer-header .ant-drawer-header-title > .ant-drawer-title {
    order: 1;
  }

  .ant-drawer-header-title {
    display: flex;
    justify-content: flex-end;
  }

  .ant-drawer-header {
    .ant-drawer-title {
      color: #4a4a68;
    }
  }
  .ant-drawer-body {
    padding-top: 24px;
  }

  .ant-drawer-header-title button.ant-drawer-close {
    background-color: #4a4a68;
  }

  > * > * > * > .ant-drawer-header-no-title {
    min-height: 110px;
  }

  .ant-drawer-body {
    max-height: calc(100vh - 108px);
    overflow-y: hidden;
    padding-right: 0;
  }

  .ant-tabs-content-holder {
    max-height: calc(100vh - 200px);
    padding-right: 16px;
  }
`

const LocationInventoryPublisher = styled.div`
  .header {
    padding: 16px 0 16px 16px;
    background: #f3f2f4;
  }
`

export default InventoryPublishDrawer
